import { Injectable } from '@angular/core';
import { TaskListParams, TaskTemplateCreateBody } from '@app/domain';
import { SubTask } from '@app/domain/entities/sub-task.entity';
import {
  Task,
  TaskDetail,
  TaskTemplateItem,
} from '@app/domain/entities/task-template.entity';
import { TaskTemplateRepository } from '@app/domain/repositories/task-template.repository';
import { PaginatedResponse } from '@app/shared/models/global.model';
import { map, Observable } from 'rxjs';
import { TaskTemplateService } from '../api-clients/task-template.service';
import { SubTaskMapper } from '../mappers/sub-task.mapper';
import { TaskTemplateMapper } from '../mappers/task-template.mapper';

@Injectable()
export class TaskTemplateRepositoryImpl implements TaskTemplateRepository {
  private readonly _taskTemplateMapper = new TaskTemplateMapper();
  private readonly _subTaskTemplateMapper = new SubTaskMapper();

  constructor(private readonly _taskTemplateService: TaskTemplateService) {}

  getList(params: TaskListParams): Observable<PaginatedResponse<Task>> {
    const tasksDto = this._taskTemplateService.getList(params);

    return tasksDto.pipe(
      map(dto => this._taskTemplateMapper.tasksListToDomain(dto))
    );
  }

  detail(taskTemplateId: number): Observable<TaskDetail> {
    const taskDetailDto = this._taskTemplateService.detail(taskTemplateId);
    return taskDetailDto.pipe(
      map(dto => this._taskTemplateMapper.taskDetailsToDomain(dto))
    );
  }

  create(body: TaskTemplateCreateBody): Observable<TaskDetail> {
    const taskDetailDto = this._taskTemplateService.create(body);
    return taskDetailDto.pipe(
      map(dto => this._taskTemplateMapper.taskDetailsToDomain(dto))
    );
  }

  update(
    taskTemplateId: number,
    body: TaskTemplateCreateBody
  ): Observable<void> {
    return this._taskTemplateService.update(taskTemplateId, body);
  }

  getAvailable(taskGroupId: string): Observable<TaskTemplateItem[]> {
    const taskTemplateItemDto =
      this._taskTemplateService.getAvailable(taskGroupId);
    return taskTemplateItemDto.pipe(
      map(dto => this._taskTemplateMapper.taskTemplateItemsListToDomain(dto))
    );
  }

  getAdded(taskGroupId: string): Observable<TaskTemplateItem[]> {
    const taskTemplateItemDto = this._taskTemplateService.getAdded(taskGroupId);
    return taskTemplateItemDto.pipe(
      map(dto => this._taskTemplateMapper.taskTemplateItemsListToDomain(dto))
    );
  }

  delete(taskTemplateId: number): Observable<void> {
    return this._taskTemplateService.delete(taskTemplateId);
  }

  getSubTasksByTaskTemplateId(taskTemplateId: number): Observable<SubTask[]> {
    const subTaskDto =
      this._taskTemplateService.getSubTasksByTaskTemplateId(taskTemplateId);

    return subTaskDto.pipe(
      map(dto => this._subTaskTemplateMapper.subTasksListToDomain(dto))
    );
  }

  createStep(taskTemplateId: number, stepData: any): Observable<any> {
    return this._taskTemplateService.createStep(taskTemplateId, stepData);
  }

  updateStep(
    taskTemplateId: number,
    stepId: number,
    stepData: any
  ): Observable<any> {
    return this._taskTemplateService.updateStep(
      taskTemplateId,
      stepId,
      stepData
    );
  }

  deleteStep(taskTemplateId: number, stepId: number): Observable<any> {
    return this._taskTemplateService.deleteStep(taskTemplateId, stepId);
  }

  updateStatus(taskTemplateId: number, isActive: boolean): Observable<void> {
    return this._taskTemplateService.updateStatus(taskTemplateId, isActive);
  }
}
